import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { AccountService } from '../account/account.service';
import { MapSkin, SettingsService, Unit } from '../account/settings.service';
import { LanguageService } from '../account/language.service';
import { Address } from '../../models/address';

export interface SettingsToolResult {
  output: Record<string, unknown>;
  stateChanged: boolean;
}

type UnitChoice = 'metric' | 'imperial';
const UNIT_KEYS = ['distance_unit', 'temperature_unit', 'speed_unit', 'pressure_unit'] as const;

/**
 * Voice assistant access to the settings dialog: language, voice guidance, map theme, map POIs and
 * units. Values are applied and stored the way the dialog stores them. The home / work address is
 * intentionally not changeable by voice, but is exposed so it can be used as a destination.
 */
@Injectable({ providedIn: 'root' })
export class RealtimeSettingsToolsService {
  private readonly accountService = inject(AccountService);
  private readonly settingsService = inject(SettingsService);
  private readonly languageService = inject(LanguageService);

  private static readonly TOOL_NAMES = new Set(['get_app_settings', 'set_app_settings']);

  public handles(name: string): boolean {
    return RealtimeSettingsToolsService.TOOL_NAMES.has(name);
  }

  public async execute(name: string, args: any): Promise<SettingsToolResult> {
    try {
      switch (name) {
        case 'get_app_settings':
          return { output: { ...this.buildSettingsState(), available_languages: this.languageService.getLanguageList() }, stateChanged: false };
        case 'set_app_settings': return await this.setAppSettings(args);
      }
    } catch (error: any) {
      console.error('Settings tool failed:', name, error);
      return this.fail('request_failed', error?.message || 'The change could not be saved. Please try again.');
    }
    return this.fail('unknown_tool', `Unknown settings tool ${name}.`);
  }

  /** Current settings dialog values; also used in the session prompt. */
  public buildSettingsState(): Record<string, unknown> {
    const unit = this.settingsService.getUnit();
    const map = this.settingsService.getMap();
    const code = this.languageService.getLanguage();
    const toChoice = (value: Unit): UnitChoice => value === Unit.Imperial ? 'imperial' : 'metric';

    return {
      language: { code, name: this.languageService.getLanguageList().find(l => l.code === code)?.name },
      voice_guidance: !this.settingsService.getVoiceGuidence().Mute,
      theme: map.Skin === MapSkin.Light ? 'light' : 'dark',
      map_pois: map.POIs,
      distance_unit: toChoice(unit.Distance),
      temperature_unit: toChoice(unit.Temperature),
      speed_unit: toChoice(unit.Speed),
      pressure_unit: toChoice(unit.Pressure),
      home_address: this.getSavedAddress('home')?.Description ?? null,
      work_address: this.getSavedAddress('work')?.Description ?? null
    };
  }

  /** The saved home / work address, available only when signed in (same rule as the search shortcuts). */
  public getSavedAddress(type: 'home' | 'work'): Address | undefined {
    if (!this.accountService.getIsAuthorized()) {
      return undefined;
    }
    const address = type === 'home' ? this.settingsService.getHomeAddress() : this.settingsService.getWorkAddress();
    return address?.Description && address.Location ? address : undefined;
  }

  public getToolDefinitions(): any[] {
    const unitParam = (what: string) => ({ type: "string", enum: ["metric", "imperial"], description: what });
    return [
      {
        type: "function",
        name: "get_app_settings",
        description: "Read the app settings (language, voice guidance, theme, map POIs, units, saved home/work address) and the list of available languages.",
        parameters: { type: "object", properties: {} }
      },
      {
        type: "function",
        name: "set_app_settings",
        description: "Change app settings; pass only what the user wants to change.",
        parameters: {
          type: "object",
          properties: {
            language: { type: "string", description: "App language: code or name from available_languages (e.g. 'de' or 'Deutsch')." },
            voice_guidance: { type: "boolean", description: "Spoken turn-by-turn navigation instructions on (true) or muted (false)." },
            theme: { type: "string", enum: ["light", "dark"], description: "Map and app colour theme." },
            map_pois: { type: "boolean", description: "Show points of interest on the map." },
            distance_unit: unitParam("km (metric) or miles (imperial)."),
            temperature_unit: unitParam("°C (metric) or °F (imperial)."),
            speed_unit: unitParam("km/h (metric) or mph (imperial)."),
            pressure_unit: unitParam("bar (metric) or psi (imperial).")
          }
        }
      }
    ];
  }

  // ---------------------------------------------------------------- settings

  private async setAppSettings(args: any): Promise<SettingsToolResult> {
    const applied: string[] = [];
    const ignored: string[] = [];

    // Units, voice guidance and map: same setters as SettingsDialogComponent.switchParamsChanged.
    const unit = this.settingsService.getUnit();
    const current: Record<typeof UNIT_KEYS[number], UnitChoice> = {
      distance_unit: unit.Distance === Unit.Imperial ? 'imperial' : 'metric',
      temperature_unit: unit.Temperature === Unit.Imperial ? 'imperial' : 'metric',
      speed_unit: unit.Speed === Unit.Imperial ? 'imperial' : 'metric',
      pressure_unit: unit.Pressure === Unit.Imperial ? 'imperial' : 'metric'
    };
    let unitsChanged = false;
    UNIT_KEYS.forEach(key => {
      if (args[key] == null) {
        return;
      }
      if (args[key] !== 'metric' && args[key] !== 'imperial') {
        ignored.push(`${key} must be metric or imperial`);
        return;
      }
      unitsChanged ||= current[key] !== args[key];
      current[key] = args[key];
      applied.push(key);
    });
    if (unitsChanged) {
      this.settingsService.setUnit(this.settingsService.unitJSONtoUnitSettings({
        distance: current.distance_unit,
        temperature: current.temperature_unit,
        speed: current.speed_unit,
        pressure: current.pressure_unit
      }));
    }

    if (typeof args.voice_guidance === 'boolean') {
      this.settingsService.setVoiceGuidence(this.settingsService.voiceGuidenceJSONtoVoiceGuidenceSettings({ mute: !args.voice_guidance }));
      applied.push('voice_guidance');
    }

    const map = this.settingsService.getMap();
    const skin = args.theme === 'light' ? MapSkin.Light : args.theme === 'dark' ? MapSkin.Dark : map.Skin;
    if (args.theme != null && args.theme !== 'light' && args.theme !== 'dark') {
      ignored.push('theme must be light or dark');
    } else if (args.theme != null) {
      applied.push('theme');
    }
    const pois = typeof args.map_pois === 'boolean' ? args.map_pois : map.POIs;
    if (typeof args.map_pois === 'boolean') {
      applied.push('map_pois');
    }
    if (skin !== map.Skin || pois !== map.POIs) {
      this.settingsService.setMap(this.settingsService.mapJSONtoMapSettings({ skin, pois }));
    }

    if (args.language != null) {
      const wanted = String(args.language).trim().toLowerCase();
      const language = this.languageService.getLanguageList()
        .find(l => l.code.toLowerCase() === wanted || l.name.toLowerCase() === wanted);
      if (language) {
        this.languageService.setLanguage(language.code);
        applied.push('language');
      } else {
        ignored.push(`unknown language "${args.language}"; use get_app_settings for the list`);
      }
    }

    if (applied.length === 0) {
      return this.fail('nothing_to_change', ignored.length > 0 ? ignored.join('; ') : 'No setting was given.');
    }

    // The dialog saves settings to the account when it closes; signed out, the setters wrote localStorage.
    if (this.accountService.getIsAuthorized()) {
      await firstValueFrom(this.accountService.setUserSettings());
    }

    return {
      output: { success: true, applied, ignored: ignored.length > 0 ? ignored : undefined, settings: this.buildSettingsState() },
      stateChanged: true
    };
  }

  private fail(error: string, message: string): SettingsToolResult {
    return { output: { success: false, error, message }, stateChanged: false };
  }
}
